// src/features/executive/sales/drill/CategoryMixDrill.tsx
import { useMemo } from 'react'
import { masterSliceLabel } from 'features/executive/shared/utils/master-slice'
import { Box, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useCategoryMix } from '../hooks/use-category-mix'
import DrillTable from 'features/executive/components/DrillTable/DrillTable'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { formatCurrency } from 'utils/executive.utils'
import { useRegisterDrillExport } from 'features/executive/components/DrillDrawer/DrillExportContext'

export default function CategoryMixDrill() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const stack = useSelector((s: { executive: ExecutiveState }) => s.executive.drillStack)
  const top = stack[stack.length - 1]
  const { data, loading, error, refetch } = useCategoryMix(top?.scopedFilters)

  // Full type list — a single-type drill goes to 'top-procedures' instead.
  const displayData = data ?? null

  const widgetTitle = top?.label ?? t('EXECUTIVE.SALES.CATEGORY_MIX.TITLE')
  // Stabilize the payload reference so the export-context's useEffect doesn't
  // re-register on every render (which would loop via the provider's setState).
  const exportPayload = useMemo(
    () => (displayData ? { drillId: 'category-mix' as const, data: displayData } : null),
    [displayData]
  )
  useRegisterDrillExport(exportPayload, 'sales.category-mix', widgetTitle)

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {top?.label ?? t('EXECUTIVE.SALES.CATEGORY_MIX.TITLE')}
      </Typography>
      <DrillTable
        rows={displayData?.categories ?? []}
        columns={[
          { key: 'name', label: t('EXECUTIVE.SALES.COLUMN.NAME'), render: (r) => masterSliceLabel(r, language) },
          { key: 'revenue', label: t('EXECUTIVE.SALES.COLUMN.REVENUE'), align: 'right', render: (r) => formatCurrency(r.amount) },
          { key: 'share', label: t('EXECUTIVE.SALES.COLUMN.SHARE'), align: 'right', render: (r) => `${r.percent.toFixed(1)}%` },
        ]}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    </Box>
  )
}
